import React, { useState, useContext } from "react";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import PageHero from "@/components/PageHero";
import { CLINIC } from "@/lib/data";
import { BookContext } from "@/components/Layout";
import { useLang } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { open } = useContext(BookContext);
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [state, setState] = useState({ submitting: false, success: false, error: "" });

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState({ submitting: true, success: false, error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setState({ submitting: false, success: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setState({ submitting: false, success: false, error: err.response?.data?.detail || "Failed to send. Please try again." });
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
        crumbs={[{ label: t("nav.contact") }]}
        image="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600&q=80"
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-7">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold">{t("contact.reach.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-deep font-semibold tracking-tight leading-tight">
              {t("contact.reach.title1")}<br /> <span className="italic text-brand">{t("contact.reach.title2")}</span>
            </h2>

            <div className="space-y-4">
              {[
                { icon: MapPin, label: t("common.address"), value: CLINIC.address },
                { icon: Phone, label: t("common.phone"), value: <><a className="hover:text-brand" href={`tel:${CLINIC.phone}`}>{CLINIC.phone}</a> <span className="text-ink-soft/40 mx-1">/</span> <a className="hover:text-brand" href={`tel:${CLINIC.altPhone}`}>{CLINIC.altPhone}</a></> },
                { icon: Mail, label: t("common.email"), value: <a className="hover:text-brand" href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> },
                { icon: Clock, label: t("common.timings"), value: t("common.timings.value") },
              ].map((it) => (
                <div key={it.label} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-black/5 shadow-soft" data-testid={`contact-${it.label.toLowerCase()}`}>
                  <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                    <it.icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <div className="text-sm">
                    <div className="text-xs tracking-[0.15em] uppercase text-ink-soft font-semibold mb-1">{it.label}</div>
                    <div className="text-deep">{it.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-wa text-white rounded-full font-medium text-sm" data-testid="wa-cta">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <button onClick={() => open()} className="inline-flex items-center gap-2 px-5 py-3 bg-brand text-white rounded-full font-medium text-sm" data-testid="contact-book-btn">
                {t("cta.book")}
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-black/5 rounded-3xl shadow-soft p-7 sm:p-10">
              <h3 className="font-display text-2xl sm:text-3xl text-deep font-semibold mb-2">{t("contact.formTitle")}</h3>
              <p className="text-sm text-ink-soft mb-7">{t("contact.formSubtitle")}</p>

              {state.success && (
                <div className="mb-5 px-5 py-4 bg-brand/5 border border-brand/15 text-brand rounded-2xl flex items-center gap-3" data-testid="contact-success">
                  <CheckCircle2 className="w-5 h-5" /> {t("contact.success")}
                </div>
              )}
              {state.error && (
                <div className="mb-5 px-5 py-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm" data-testid="contact-error">{state.error}</div>
              )}

              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="contact-form">
                <Field label={t("form.fullName")} required><input required value={form.name} onChange={change("name")} className="ci" data-testid="contact-name" /></Field>
                <Field label={t("form.phone")} required><input required type="tel" value={form.phone} onChange={change("phone")} className="ci" data-testid="contact-phone" /></Field>
                <Field label={t("form.email")} className="sm:col-span-2"><input type="email" value={form.email} onChange={change("email")} className="ci" data-testid="contact-email" /></Field>
                <Field label={t("form.subject")} className="sm:col-span-2"><input value={form.subject} onChange={change("subject")} className="ci" data-testid="contact-subject" /></Field>
                <Field label={t("form.message")} required className="sm:col-span-2">
                  <textarea required rows={5} value={form.message} onChange={change("message")} className="ci resize-none" data-testid="contact-message" />
                </Field>
                <button type="submit" disabled={state.submitting} className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-deep text-white rounded-full font-semibold hover:bg-deep-700 disabled:opacity-60 transition-all" data-testid="contact-submit">
                  {state.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("form.sending")}</> : <><Send className="w-4 h-4" /> {t("form.send")}</>}
                </button>
              </form>
              <style>{`.ci{width:100%;padding:0.75rem 1rem;background:#FAFAF8;border:1px solid #E2E8F0;border-radius:0.85rem;font-size:0.875rem;color:#0B192C;outline:none;transition:all 0.2s;font-family:inherit}.ci:focus{border-color:#1C6152;background:#fff;box-shadow:0 0 0 4px rgba(28,97,82,0.08)}`}</style>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden border border-black/5 shadow-soft">
            <iframe
              title="Map"
              src="https://www.google.com/maps?q=Shri+Anandpur+Trust+Charitable+Diagnostic+Centre+Krishna+Nagar+Delhi&output=embed"
              className="w-full h-[500px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold text-deep flex items-center gap-1.5 mb-1.5">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}
